import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { httpsCallable } from 'firebase/functions';
import { firebaseService, getFirebaseFunctions } from '../services/firebase.service';
import './ContactPage.css';

export const ContactPage: React.FC = () => {
  const intl = useIntl();
  const [formData, setFormData] = useState({
    reason: 'general',
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [sending, setSending] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    if (!formData.name || !formData.email || !formData.message) {
      setError(intl.formatMessage({ id: 'landing.contact.form.errors.required' }));
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError(intl.formatMessage({ id: 'landing.contact.form.errors.invalidEmail' }));
      return;
    }

    setSending(true);
    try {
      // Ensure Firebase is initialized before proceeding
      await firebaseService.initialize();
      
      const functions = getFirebaseFunctions();
      const sendEmail = httpsCallable(functions, 'sendContactEmail');
      await sendEmail({
        app: 'Landing',
        page: 'Contact',
        formData,
      });
      
      setSubmitted(true);
      setFormData({
        reason: 'general',
        name: '',
        email: '',
        phone: '',
        message: ''
      });
    } catch (error) {
      console.error('Failed to send message:', error);
      setError(intl.formatMessage({ id: 'landing.contact.form.errors.sendFailed' }));
    } finally {
      setSending(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="contact-page">
      {/* Header with Logo and Navigation */}
      <header className="contact-header">
        <Link to="/" className="contact-logo">
          <img src="/logo-transparent-no-slogan.png" alt={intl.formatMessage({ id: 'landing.brand.alt' })} />
        </Link>
        <Link to="/" className="back-link">
          {intl.formatMessage({ id: 'landing.contact.backToHome' })}
        </Link>
      </header>

      {/* Contact Form Section */}
      <section className="contact-section">
        <div className="contact-container">
          <div className="contact-content">
            <div className="contact-info">
              <h1>{intl.formatMessage({ id: 'landing.contact.info.title' })}</h1>
              <p className="contact-subtitle">
                {intl.formatMessage({ id: 'landing.contact.info.subtitle' })}
              </p>
              
              <div className="contact-details">
                <div className="detail-item">
                  <span className="detail-icon">📧</span>
                  <div>
                    <h3>{intl.formatMessage({ id: 'landing.contact.info.email.title' })}</h3>
                    <p>{intl.formatMessage({ id: 'landing.contact.info.email.address' })}</p>
                  </div>
                </div>
                
                <div className="detail-item">
                  <span className="detail-icon">💬</span>
                  <div>
                    <h3>{intl.formatMessage({ id: 'landing.contact.info.response.title' })}</h3>
                    <p>{intl.formatMessage({ id: 'landing.contact.info.response.body' })}</p>
                  </div>
                </div>
                
                <div className="detail-item">
                  <span className="detail-icon">🌍</span>
                  <div>
                    <h3>{intl.formatMessage({ id: 'landing.contact.info.coverage.title' })}</h3>
                    <p>{intl.formatMessage({ id: 'landing.contact.info.coverage.body' })}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="contact-form-wrapper">
              {submitted ? (
                <div className="success-message">
                  <div className="success-icon">✓</div>
                  <h2>{intl.formatMessage({ id: 'landing.contact.success.title' })}</h2>
                  <p>{intl.formatMessage({ id: 'landing.contact.success.message' })}</p>
                  <button
                    onClick={() => setSubmitted(false)}
                    className="btn-primary"
                  >
                    {intl.formatMessage({ id: 'landing.contact.success.sendAnother' })}
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="contact-form">
                  <h2>{intl.formatMessage({ id: 'landing.contact.form.title' })}</h2>
                  
                  {error && (
                    <div className="error-message">
                      {error}
                    </div>
                  )}

                  <div className="form-group">
                    <label htmlFor="reason">
                      {intl.formatMessage({ id: 'landing.contact.form.reason.label' })}{' '}
                      <span className="required">{intl.formatMessage({ id: 'landing.contact.form.reason.required' })}</span>
                    </label>
                    <select
                      id="reason"
                      name="reason"
                      value={formData.reason}
                      onChange={handleChange}
                      required
                    >
                      <option value="general">{intl.formatMessage({ id: 'landing.contact.form.reason.options.general' })}</option>
                      <option value="stream-owner">{intl.formatMessage({ id: 'landing.contact.form.reason.options.streamOwner' })}</option>
                      <option value="fan">{intl.formatMessage({ id: 'landing.contact.form.reason.options.fan' })}</option>
                      <option value="spotter">{intl.formatMessage({ id: 'landing.contact.form.reason.options.spotter' })}</option>
                    </select>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="name">
                        {intl.formatMessage({ id: 'landing.contact.form.name.label' })}{' '}
                        <span className="required">{intl.formatMessage({ id: 'landing.contact.form.name.required' })}</span>
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        placeholder={intl.formatMessage({ id: 'landing.contact.form.name.placeholder' })}
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="email">
                        {intl.formatMessage({ id: 'landing.contact.form.email.label' })}{' '}
                        <span className="required">{intl.formatMessage({ id: 'landing.contact.form.email.required' })}</span>
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder={intl.formatMessage({ id: 'landing.contact.form.email.placeholder' })}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="phone">
                      {intl.formatMessage({ id: 'landing.contact.form.phone.label' })}{' '}
                      <span className="optional">{intl.formatMessage({ id: 'landing.contact.form.phone.optional' })}</span>
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder={intl.formatMessage({ id: 'landing.contact.form.phone.placeholder' })}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="message">
                      {intl.formatMessage({ id: 'landing.contact.form.message.label' })}{' '}
                      <span className="required">{intl.formatMessage({ id: 'landing.contact.form.message.required' })}</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={6}
                      placeholder={intl.formatMessage({ id: 'landing.contact.form.message.placeholder' })}
                    />
                  </div>

                  <button
                    type="submit"
                    className="btn-submit"
                    disabled={sending}
                  >
                    {sending ? (
                      <>
                        <span className="spinner"></span>
                        {intl.formatMessage({ id: 'landing.contact.form.sending' })}
                      </>
                    ) : (
                      <>
                        {intl.formatMessage({ id: 'landing.contact.form.submit' })}
                        <span className="arrow">→</span>
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

